/**
 * Elegibilidade noturna de uma paragem de visita. Puro, sem IO.
 *
 * Regras do plano:
 * - Exterior aberto sem horários confirmados → "vista exterior noturna"
 *   explícita (nunca uma visita "normal" fechada);
 * - Interior (museu, igreja, castelo…) só é visita noturna com horários
 *   confirmados para a noite; caso contrário fica na parte diurna do dia;
 * - Restaurantes/bares/hotéis nunca são "experiência noturna" de visita.
 */

export type OpeningPeriod = {
  open?: { day?: number; hour?: number; minute?: number };
  close?: { day?: number; hour?: number; minute?: number };
};

export type EveningCandidate = {
  name: string;
  placeTypes?: string[];
  placePrimaryType?: string;
  category?: string;
  /** Períodos do Google Places quando existem; null/undefined = sem horários. */
  openingPeriods?: OpeningPeriod[] | null;
  lat?: number;
  lng?: number;
};

export type EveningSuitability = "open_evening" | "outdoor_view" | "daytime_only";

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/[_-]+/gu, " ")
    .trim();

const OUTDOOR_TYPES = new Set([
  "viewpoint", "scenic viewpoint", "observation deck", "lookout", "miradouro",
  "park", "urban park", "garden", "botanical garden", "plaza", "square", "piazza",
  "bridge", "pier", "promenade", "waterfront", "marina", "harbor", "port",
  "fountain", "memorial", "monument", "statue", "obelisk", "beach", "river",
  "canal", "lake", "reservoir", "rooftop", "terrace", "amphitheatre",
  "amphitheater", "ruins", "archaeological site", "lighthouse", "fortress walls",
  "city walls", "stairway", "stairs", "cable car", "funicular",
]);

const INDOOR_TYPES = new Set([
  "museum", "art gallery", "gallery", "church", "cathedral", "basilica",
  "mosque", "synagogue", "temple", "shrine", "palace", "castle", "monastery",
  "convent", "aquarium", "zoo", "theater", "theatre", "opera house", "cinema",
  "shopping mall", "market", "library", "stadium", "arena", "planetarium",
  "exhibition centre", "exhibition center", "cultural center", "cultural centre",
]);

const NON_STOP_TYPES = new Set([
  "restaurant", "cafe", "bar", "pub", "night club", "nightclub", "hotel",
  "lodging", "meal takeaway", "food", "bakery", "guest house", "hostel",
]);

const typeOf = (candidate: EveningCandidate): string => {
  const primary = fold(candidate.placePrimaryType ?? "");
  if (primary) return primary;
  const fromTypes = (candidate.placeTypes ?? []).map(fold).find((t) =>
    OUTDOOR_TYPES.has(t) || INDOOR_TYPES.has(t) || NON_STOP_TYPES.has(t),
  );
  return fromTypes ?? fold(candidate.category ?? "");
};

const isAnyType = (candidate: EveningCandidate, set: Set<string>): boolean => {
  if (set.has(typeOf(candidate))) return true;
  return (candidate.placeTypes ?? []).some((t) => set.has(fold(t)));
};

const isNonStop = (candidate: EveningCandidate): boolean => isAnyType(candidate, NON_STOP_TYPES);

/**
 * Horários confirmados cobrem alguma parte da noite (19:00–23:00) ou
 * fecham após a meia-noite num qualquer dia da semana.
 */
export function hasConfirmedEveningHours(periods: OpeningPeriod[] | null | undefined): boolean {
  if (!periods?.length) return false;
  return periods.some((period) => {
    const openHour = period.open?.hour;
    const closeHour = period.close?.hour;
    if (typeof openHour !== "number") return false;
    // Fechado depois da meia-noite (close.day > open.day ou close.hour === 0).
    if (typeof closeHour === "number") {
      if (closeHour === 0 || (period.close?.day ?? 0) > (period.open?.day ?? 0)) return true;
      if (closeHour >= 21 && openHour <= 20) return true;
      // Janela que atravessa o início da noite (abre 17h, fecha 20h+).
      if (openHour <= 19 && closeHour >= 21) return true;
      return false;
    }
    return openHour <= 19;
  });
}

/** Classificação noturna de uma paragem candidata. */
export function classifyEveningSuitability(candidate: EveningCandidate): EveningSuitability {
  if (isNonStop(candidate)) {
    // Bares/terraços podem ser abertos à noite, mas nunca são paragem de
    // visita; a classificação importa apenas para quem já é paragem.
    return hasConfirmedEveningHours(candidate.openingPeriods) ? "open_evening" : "daytime_only";
  }
  const eveningHours = hasConfirmedEveningHours(candidate.openingPeriods);
  if (eveningHours) return "open_evening";
  if (isAnyType(candidate, OUTDOOR_TYPES)) return "outdoor_view";
  return "daytime_only";
}

/** Pode ser apresentada como experiência noturna (aberta ou vista exterior). */
export function isEveningEligible(candidate: EveningCandidate): boolean {
  const level = classifyEveningSuitability(candidate);
  return level === "open_evening" || level === "outdoor_view";
}

/** Nota explícita mostrada na paragem quando é experiência noturna. */
export function eveningExperienceLabel(level: EveningSuitability, language = "pt"): string {
  const key = (language || "pt").slice(0, 2).toLowerCase();
  if (level === "open_evening") {
    const dict: Record<string, string> = {
      pt: "Aberto à noite",
      en: "Open in the evening",
      es: "Abierto por la noche",
      fr: "Ouvert le soir",
      it: "Aperto la sera",
      de: "Abends geöffnet",
    };
    return dict[key] ?? dict.en;
  }
  if (level === "outdoor_view") {
    const dict: Record<string, string> = {
      pt: "Vista exterior noturna",
      en: "Evening exterior view",
      es: "Vista exterior nocturna",
      fr: "Vue extérieure nocturne",
      it: "Vista esterna notturna",
      de: "Abendliche Außenansicht",
    };
    return dict[key] ?? dict.en;
  }
  const dict: Record<string, string> = {
    pt: "Visita diurna",
    en: "Daytime visit",
    es: "Visita diurna",
    fr: "Visite de jour",
    it: "Visita diurna",
    de: "Tagesbesuch",
  };
  return dict[key] ?? dict.en;
}

/**
 * Escolhe a experiência noturna do dia: prefere locais abertos à noite com
 * horários confirmados; sem esses, uma vista exterior sem horários. Devolve
 * null quando nenhum candidato é elegível.
 */
export function pickEveningStop<T>(
  dayStops: T[],
  to: (stop: T) => EveningCandidate | null,
): { stop: T; level: EveningSuitability } | null {
  let outdoor: { stop: T } | null = null;
  for (const stop of dayStops) {
    const candidate = to(stop);
    if (!candidate || isNonStop(candidate)) continue;
    const level = classifyEveningSuitability(candidate);
    if (level === "open_evening") return { stop, level };
    if (level === "outdoor_view" && !outdoor) outdoor = { stop };
  }
  return outdoor ? { stop: outdoor.stop, level: "outdoor_view" } : null;
}

/* ------------------------------ Sol (NOAA) ------------------------------ */

const DEG = Math.PI / 180;

/**
 * Minutos UTC do pôr do sol para uma data (aproximação NOAA, ±2 min).
 * Devolve null nos casos polares (sol sempre acima/abaixo do horizonte).
 */
export function sunsetMinutesUTC(lat: number, lng: number, date: Date): number | null {
  const rad = DEG;
  const msPerDay = 86400000;
  const n = Math.floor((Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - Date.UTC(2000, 0, 1, 12)) / msPerDay);
  const jStar = n - lng / 360;
  const m = (357.5291 + 0.98560028 * jStar) % 360;
  const c = 1.9148 * Math.sin(m * rad) + 0.02 * Math.sin(2 * m * rad) + 0.0003 * Math.sin(3 * m * rad);
  const lambda = (m + c + 180 + 102.9372) % 360;
  const jTransit = jStar + 0.0053 * Math.sin(m * rad) - 0.0069 * Math.sin(2 * lambda * rad);
  const delta = Math.asin(Math.sin(lambda * rad) * Math.sin(23.4397 * rad));
  const cosOmega =
    (Math.sin(-0.833 * rad) - Math.sin(lat * rad) * Math.sin(delta)) /
    (Math.cos(lat * rad) * Math.cos(delta));
  if (cosOmega > 1 || cosOmega < -1) return null; // noite/sol da meia-noite
  const jSet = jTransit + Math.acos(cosOmega) / DEG / 360;
  const fraction = jSet - Math.floor(jSet);
  // jSet é medido em dias desde o meio-dia UTC de referência (J2000.0):
  // fração 0 = 12:00 UTC, pelo que o pôr do sol é 12:00 + fração do dia.
  return Math.round((fraction * 1440 + 720) % 1440);
}

/** Minutos locais do pôr do sol, dado o offset UTC do destino em minutos. */
export function sunsetMinutesLocal(
  lat: number,
  lng: number,
  date: Date,
  utcOffsetMinutes: number,
): number | null {
  const utc = sunsetMinutesUTC(lat, lng, date);
  if (utc == null) return null;
  return ((utc + utcOffsetMinutes) % 1440 + 1440) % 1440;
}
